/**
 * Centralized Analytics Event Manager
 * Handles both Google Analytics and Facebook Pixel tracking
 */

// Safe event tracking wrapper
const trackEvent = (eventName, parameters = {}) => {
	try {
		// Google Analytics tracking
		if (window.gtag) {
			window.gtag('event', eventName, parameters);
		}
		
		// Facebook Pixel tracking
		// Use standard 'PageView' for page views; for all other non-standard events use trackCustom
		if (window.fbq) {
			if (eventName === 'page_view') {
				window.fbq('track', 'PageView', parameters);
			} else {
				window.fbq('trackCustom', eventName, parameters);
			}
		}
	} catch (error) {
		console.warn('Analytics tracking error:', error);
	}
};

// Track WebKit errors that could affect paid traffic conversion
export const trackWebKitErrorsPrevented = (errorCount, userAgent) => {
	try {
		trackEvent('webkit_errors_prevented', {
			event_category: 'paid_traffic_protection',
			event_label: 'facebook_inapp_browser',
			value: errorCount,
			custom_parameters: {
				user_agent: userAgent,
				error_type: 'webkit_messagehandlers',
				traffic_source: 'facebook_ads'
			}
		});
		
		// Also track as a Facebook Pixel custom event for ROI analysis
		if (window.fbq) {
			window.fbq('trackCustom', 'WebKitErrorsPrevented', {
				content_category: 'Technical Issues',
				content_name: 'WebKit MessageHandlers Error',
				value: errorCount,
				currency: 'USD' // For cost analysis
			});
		}
	} catch (error) {
		console.warn('WebKit error tracking failed:', error);
	}
};

// Track successful app initialization in problematic browsers
export const trackInAppBrowserSuccess = (browserType) => {
	try {
		trackEvent('inapp_browser_success', {
			event_category: 'paid_traffic_success',
			event_label: browserType,
			custom_parameters: {
				browser_type: browserType,
				webkit_protection: 'enabled'
			}
		});
	} catch (error) {
		console.warn('In-app browser success tracking failed:', error);
	}
};

// Track page views
export const trackPageView = (pageName, additionalData = {}) => {
	trackEvent('page_view', {
		page_title: pageName,
		...additionalData
	});
};

// Track user registration events
export const trackRegistration = (method = 'email') => {
	trackEvent('sign_up', {
		method: method,
		content_category: 'Authentication'
	});
	
	// Facebook Pixel specific event
	if (window.fbq) {
		window.fbq('track', 'CompleteRegistration', {
			content_name: 'User Registration',
			content_category: 'Authentication',
			source: method
		});
	}
};

// Track login events
export const trackLogin = (method = 'email') => {
	trackEvent('login', {
		method: method,
		content_category: 'Authentication'
	});
	
	// Facebook Pixel specific event
	if (window.fbq) {
		window.fbq('track', 'Lead', {
			content_name: 'User Login',
			content_category: 'Authentication',
			source: method
		});
	}
};

// Track OTP request
export const trackOTPRequest = (email) => {
	trackEvent('generate_lead', {
		content_category: 'Authentication',
		method: 'email_otp',
		user_email: email
	});
	
	// Facebook Pixel Lead event
	if (window.fbq) {
		window.fbq('track', 'Lead', {
			content_name: 'OTP Request',
			content_category: 'Authentication',
			source: 'email_otp'
		});
	}
};

// Track chat interactions
export const trackChatMessage = (characterName = '') => {
	trackEvent('chat_message', {
		content_category: 'Chat Engagement',
		character_name: characterName
	});
	
	// Facebook Pixel Contact event
	if (window.fbq) {
		window.fbq('track', 'Contact', {
			content_name: 'Chat Message',
			content_category: 'Chat Engagement',
			character_name: characterName
		});
	}
};

// Track character selection
export const trackCharacterSelect = (characterId, characterName) => {
	trackEvent('select_content', {
		content_type: 'character',
		content_id: characterId,
		content_name: characterName
	});
	
	// Facebook Pixel ViewContent event
	if (window.fbq) {
		window.fbq('track', 'ViewContent', {
			content_name: characterName,
			content_category: 'Character Selection',
			content_id: characterId
		});
	}
};

// Track subscription/purchase events
export const trackSubscription = (planName, value, currency = 'TRY') => {
	trackEvent('purchase', {
		transaction_id: Date.now().toString(),
		value: value,
		currency: currency,
		items: [{
			item_id: planName,
			item_name: planName,
			category: 'Subscription',
			quantity: 1,
			price: value
		}]
	});
	
	// Facebook Pixel Purchase event
	if (window.fbq) {
		window.fbq('track', 'Purchase', {
			content_name: planName,
			content_category: 'Subscription',
			value: value,
			currency: currency
		});
	}
};

// Track button clicks
export const trackButtonClick = (buttonName, location = '') => {
	trackEvent('click', {
		content_category: 'UI Interaction',
		button_name: buttonName,
		location: location
	});
};

// Track modal interactions
export const trackModalOpen = (modalName) => {
	trackEvent('modal_open', {
		content_category: 'UI Interaction',
		modal_name: modalName
	});
};

export const trackModalClose = (modalName) => {
	trackEvent('modal_close', {
		content_category: 'UI Interaction',
		modal_name: modalName
	});
};

// Track form submissions
export const trackFormSubmit = (formName, success = true) => {
	trackEvent('form_submit', {
		content_category: 'Form Interaction',
		form_name: formName,
		success: success
	});
};

// Track errors
export const trackError = (errorType, errorMessage = '') => {
	trackEvent('exception', {
		description: errorMessage,
		fatal: false,
		error_type: errorType
	});
};

// Track user journey milestones
export const trackUserJourney = (milestone) => {
	trackEvent('user_journey', {
		content_category: 'User Journey',
		milestone: milestone
	});
};

// Track referral detections (social/ad referrals)
export const trackReferral = (source = '', details = {}) => {
	try {
		trackEvent('referral_detected', {
			content_category: 'Acquisition',
			source: source,
			...details
		});

		// Also send a Facebook Pixel custom event if available
		if (window.fbq) {
			window.fbq('trackCustom', 'ReferralDetected', {
				source: source,
				...details
			});
		}
	} catch (error) {
		console.warn('Referral tracking failed:', error);
	}
};
